import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNewOperationDate } from 'utils/getNewOperationDate';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';

import { cleanValue } from 'constants/formats';
import { addPartnerOption } from 'constants/addPartnerOption';
import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import transactionTypes from 'constants/transactionTypes';

import {
  HistoryMovement,
  Movement,
  Partner,
  SelectorOption,
  Transaction,
} from 'types';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetHoldingClassOptions } from 'modules/partners/hooks/useGetHoldingClassOption';
import { useGetPartnerOptionsForOperationModal } from 'modules/transactions/hooks/useGetPartnerOptionsForOperationModal';
import { createMovement } from 'modules/transactions/utils/createMovement';
import { createTransaction } from 'modules/transactions/utils/createTransaction';
import { useGetAvailableRangeByHoldingClass } from 'modules/_shared/hooks/useGetAvailableRangeByHoldingClass';
import movementTypes from 'modules/transactions/constants/movementTypes';

import { buildMockDataFromBaseData } from '../utils/buildMockDataFromBaseData';
import { baseDataForCapitalIncrease } from '../constants/baseDataForCapitalIncrease';

import { CapitalIncreaseModalContextType } from '../types/CapitalIncreaseModalContextType';
import { capitalIncreaseModalInitValue } from '../constants/capitalIncreaseModalInitValue';

const CapitalIncreaseModalContext =
  createContext<CapitalIncreaseModalContextType>(capitalIncreaseModalInitValue);

export const CapitalIncreaseModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [date, setDate] = useState<string>('');
  const [scriptureView, setScriptureView] = useState<boolean>(true);
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');
  const [totalSharesToAdd, setTotalSharesToAdd] = useState<number>(0);
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [newPartner, setNewPartner] = useState<Partner | null>(null);
  const [showAddPartnerSection, setShowAddPartnerSection] =
    useState<boolean>(false);
  const [sharesToAdd, setSharesToAdd] = useState<number>(0);
  const [remainingShares, setRemainingShares] = useState<number>(0);
  const [hasPremium, setHasPremium] = useState<boolean>(false);
  const [premium, setPremium] = useState<number>(0);
  const [historyMovements, setHistoryMovements] = useState<HistoryMovement[]>(
    []
  );
  const [movements, setMovements] = useState<Movement[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isValidateRangeEnabled, setIsValidateRangeEnabled] =
    useState<boolean>(false);
  const [useFirstAvailableRange, setUseFirstAvailableRange] =
    useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptionsForOperationModal({
      societyId: actualSociety?.['_id'],
      newPartnerFirst: newPartner,
      isSingleSelect: true,
    });

  const { data: holdingClassOptionsData, actions: holdingClassOptionsActions } =
    useGetHoldingClassOptions({
      societyId: actualSociety?._id,
      includeShares: false,
    });

  const {
    data: availableRangeData,
    actions: availableRangeActions,
    isLoading: isLoadingRange,
  } = useGetAvailableRangeByHoldingClass();

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (comment: string) => {
    setComments(comment);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const handleChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const handleChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const handleChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleChangeTotalSharesToAdd = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const cleanedValue = cleanValue(event.target.value);
    setTotalSharesToAdd(cleanedValue);
    setRemainingShares(cleanedValue);
  };

  const handleChangePartner = (selectedOption: SelectorOption | null) => {
    setSelectedPartner(selectedOption?.value || '');
    partnerOptionsActions.handleChangeSelectedPartnerFirstOption(
      selectedOption
    );
    setShowAddPartnerSection(selectedOption?.value === addPartnerOption.value);
  };

  const handleAddPartner = (addedPartner: Partner) => {
    setNewPartner(addedPartner);
  };

  const handleCloseAddPartnerSection = () => {
    if (selectedPartner === addPartnerOption.value) {
      setSelectedPartner('');
      handleChangePartner(null);
    }
    setShowAddPartnerSection(false);
  };

  const handleChangeSharesToAdd = (event: ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = cleanValue(event.target.value);
    setSharesToAdd(cleanedValue);
    setIsValidateRangeEnabled(true);
  };

  const handleChangeHasPremium = () => {
    setHasPremium((prev) => !prev);
    setPremium(0);
  };

  const handleChangePremium = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const cleanedValue = cleanValue(event.target.value);
    setPremium(cleanedValue);
  };

  const handleSaveMovement = () => {
    const range = {
      ...availableRangeData.availableRange,
      shareClass: holdingClassOptionsData.selectedHoldingClass,
      sharePremium: premium,
      partner: selectedPartner,
    };
    const movement = createMovement({
      range,
      movementType: movementTypes.INCREMENT,
    });
    const transaction = createTransaction({
      range,
      transactionType: transactionTypes.CAPITAL_INCREASE,
    });

    setMovements([...movements, movement]);
    setTransactions([...transactions, transaction]);

    const historyMovement: HistoryMovement = {
      from: range.from,
      to: range.to,
      holdingClassName:
        holdingClassOptionsData.selectedHoldingClassOption?.label || '',
      partnerNameSeller: partnerOptionsData.selectedPartnerFirstOption?.label
        .split('|')[1]
        .trim(),
    };

    setHistoryMovements([historyMovement, ...historyMovements]);

    availableRangeActions.handleAddRange({
      from: range.from,
      to: range.to,
      shareClass: holdingClassOptionsData.selectedHoldingClass,
    });

    setSharesToAdd(0);
    setRemainingShares((prev) => prev - sharesToAdd);
  };

  const handleChangeHoldingClass = (selectedOption: SelectorOption) => {
    holdingClassOptionsActions.handleChangeSelectedHoldingClass(selectedOption);
    setIsValidateRangeEnabled(false);
    availableRangeActions.handleValidateRange({
      holdingClassId: selectedOption?.value || '',
      sharesCount: sharesToAdd,
      useFirstAvailableRange,
    });
  };

  const handleValidateRange = () => {
    setIsValidateRangeEnabled(false);
    availableRangeActions.handleValidateRange({
      holdingClassId: holdingClassOptionsData.selectedHoldingClass,
      sharesCount: sharesToAdd,
      useFirstAvailableRange,
    });
  };

  const handleChangeUseFirstAvailableRange = () => {
    setUseFirstAvailableRange((prev) => !prev);
    setIsValidateRangeEnabled(false);
    availableRangeActions.handleValidateRange({
      holdingClassId: holdingClassOptionsData.selectedHoldingClass,
      sharesCount: sharesToAdd,
      useFirstAvailableRange: !useFirstAvailableRange,
    });
  };

  const handleSaveCapitalIncrease = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);

        let uploadedDocument = null;

        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: operationTypes.CONTRIBUTION_FILENAME,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }

        const newOperationDate = getNewOperationDate(lastOperationDate, date);

        const operationData = {
          movements,
          transactions,
          date: newOperationDate,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          notarialRegistration: {
            notaryName,
            notaryCity,
            protocolNr: notaryProtocol,
          },
          operationType: operationTypes.CAPITAL_INCREASE,
        };

        await store.dispatch(addOperation(operationData));
        setIsLoading(false);

        successSwal(t('CapitalIncreaseAddedSuccessfully'));
      } catch (error) {
        errorSwal(t('ErrorAddingCapitalIncrease'));
      } finally {
        handleCloseModal();
      }
    }
  };

  const handleUploadMock = useCallback(() => {
    if (baseDataForCapitalIncrease.partners.length === 0) return;

    const {
      movements: mockMovements,
      historyMovements: mockHistory,
      transactions: mockTransactions,
      totalSharesToAdd: mockTotal,
    } = buildMockDataFromBaseData(baseDataForCapitalIncrease);

    setMovements(mockMovements);
    setHistoryMovements(mockHistory);
    setTransactions(mockTransactions);
    setTotalSharesToAdd(mockTotal);
    setRemainingShares(0);
    setSharesToAdd(0);
    setScriptureView(false);

    if (!date) {
      setDate(new Date().toISOString().slice(0, 10));
    }

    holdingClassOptionsActions.handleChangeSelectedHoldingClass({
      value: baseDataForCapitalIncrease.shareClassId,
      label: baseDataForCapitalIncrease.holdingClassName,
    });
  }, [holdingClassOptionsActions, date]);

  return (
    <CapitalIncreaseModalContext.Provider
      value={{
        data: {
          date,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          scriptureView,
          lastOperationDate: formattedLastOperationDate,
          totalSharesToAdd,
          selectedPartner,
          partnerOptions: partnerOptionsData.partnerFirstOptions,
          showAddPartnerSection,
          sharesToAdd,
          remainingShares,
          hasPremium,
          premium,
          selectedHoldingClass: holdingClassOptionsData.selectedHoldingClass,
          selectedHoldingClassOption:
            holdingClassOptionsData.selectedHoldingClassOption,
          holdingClassOptions: holdingClassOptionsData.holdingClassOptions,
          availableRange: availableRangeData.availableRange,
          useFirstAvailableRange,
          historyMovements,
          isValidateRangeEnabled,
          isLoadingRange,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          handleChangeNotaryName,
          handleChangeNotaryProtocol,
          handleChangeNotaryCity,
          handleChangeTotalSharesToAdd,
          handleChangePartner,
          handleSearchPartnerByTerm: partnerOptionsActions.fetchPartnersByTerm,
          handleAddPartner,
          handleCloseAddPartnerSection,
          handleChangeSharesToAdd,
          handleChangeHasPremium,
          handleChangePremium,
          handleChangeHoldingClass,
          handleFetchHoldingClassByTerm:
            holdingClassOptionsActions.handleSearchHoldingClass,
          handleValidateRange,
          handleChangeUseFirstAvailableRange,
          handleSaveMovement,
          handleSaveCapitalIncrease,
          handleUploadMock,
        },
      }}
    >
      {children}
    </CapitalIncreaseModalContext.Provider>
  );
};

export const useCapitalIncreaseModal = () => {
  const context = useContext(CapitalIncreaseModalContext);
  if (!context) {
    throw new Error(
      'useCapitalIncreaseModal must be used within a CapitalIncreaseModalProvider'
    );
  }
  return context;
};
